import React, {useState} from 'react';
import {FormikContextType, FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import Form, {
  FormBlock,
  FormSection,
  TextAreaField,
  TextField
} from "components/Form/Form";
import {MapEditor} from "components/MapEditor";
import {Feature, FeatureCollection, Geometry, Polygon} from "geojson";
import {ExclamationCircleIcon, PencilIcon} from "@heroicons/react/solid";
import {feature, featureCollection} from "../../../services/geometry";
import * as Yup from "yup";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {InteractionArea, InteractionAreaData} from "../../../types/InteractionArea";
import Selector from "../../../components/Form/Selector";
import IconName from "../../../types/IconName";
import {useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {useAuth} from "../../../context/AuthContext";

// Types ----

type InteractionAreaFormProps = {
  interactionArea?: InteractionArea
  onSubmit: (values: InteractionAreaData, helpers: FormikHelpers<InteractionAreaData>) => Promise<any>
}

// Helpers ----

function siteAreaFeatures(siteUuid?: string, area?: Polygon | null): FeatureCollection {
  const features = [];

  if(area) features.push(feature(siteUuid ?? '', {}, area))

  return featureCollection(features);
}

// Component ----

function InteractionAreaForm({interactionArea, onSubmit}: InteractionAreaFormProps) {
  const {termFor} = useOperator();
  const {can} = useAuth();

  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();

  const {data: sites, isLoading: sitesLoading} = useQuery(
    ['sites.all', {page: 1}],
    () => ngage.sites.all(1),
    {enabled: can('sites:read')}
  );

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    description: Yup.string().max(50000, 'Maximum character limit exceeded'),
    site_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    area: Yup.object().shape({
      coordinates: Yup.array().of(
        Yup.array().of(
          Yup.array().of(Yup.number())
        )
      ).required(`Please draw the Interaction Area on the map using the drawing tool in the top left corner`),
    }),
  })

  const initialValues: InteractionAreaData = {
    name: interactionArea?.name || '',
    description: interactionArea?.description || '',
    site_id: interactionArea?.site_id || 0,
    area: interactionArea?.area || undefined,
  }

  function handleFeaturesChanged(allFeatures: Array<Feature>, form: FormikContextType<InteractionAreaData>) {
    const area = allFeatures.find(feature => feature.geometry.type === 'Polygon')?.geometry ?? null;

    void form.setFieldValue('area', area);
  }

  function handleSubmit(values: InteractionAreaData, helpers: FormikHelpers<InteractionAreaData>) {
    return onSubmit(values, helpers)
  }

  function selectSite(siteId: number | string, {setFieldValue}: FormikHelpers<any>) {
    void setFieldValue('site_id', Number(siteId));

    // Find the site and fly to it on the map
    const site = sites?.data.find(site => site.id === Number(siteId));
    if (site) setMapFlyToGeometry(site.area);
  }

  return (
    <Form initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
      <FormSection
        name="Key Information"
        description={'Basic details about the Interaction Area'}
        columns={6}
      >
        {/* Name */}
        <FormBlock columns={3}>
          <TextField
            name="name"
            label="Name *"
            hint="255 chars max"
            help={'A recognisable name for this Interaction Area'}
            autoComplete="off"
            maxLength={255}
          />
        </FormBlock>

        {/* Description */}
        <FormBlock columns={6}>
          <TextAreaField
            name="description"
            label="Description"
            hint="Optional. 50,000 chars max."
            help={`General descriptive information about this Interaction Area`}
            autoComplete="off"
            maxChars={50000}
            rows={6}
          />
        </FormBlock>
      </FormSection>

      <FormSection
        name="Location"
        description={'Set where the Interaction Area is located'}
        columns={6}
      >

        {/* Site */}
        <FormBlock columns={3}>
          {(formik) => (
            <>
              <Selector
                label={`${termFor('Site')} *`}
                items={sites?.data.map(site => ({
                  id: site.id,
                  name: site.name,
                  icon: IconName.OFFICE_BUILDING
                })) || []}
                selectedId={initialValues.site_id}
                onChange={selected => selectSite(selected.id, formik)}
                isLoading={sitesLoading}
              />

              {(!!formik.errors?.site_id && !!formik.touched?.site_id) &&
                <FieldHelp isError>
                  {formik.errors.site_id}
                </FieldHelp>
              }
            </>
          )}
        </FormBlock>

        {/* Area */}
        <FormBlock columns={6}>
          {form => (
            <>
              <MapEditor
                height="32rem"
                tools={{polygon: true}}
                features={siteAreaFeatures(interactionArea?.uuid, form.values.area)}
                onFeaturesChanged={allFeatures => handleFeaturesChanged(allFeatures, form)}
                singleFeature
                flyToGeometry={mapFlyToGeometry}
              />
              <p className="mt-2 text-sm text-gray-500 flex items-center">
                <PencilIcon className="text-gray-600 h-4 w-4 mr-1"/>
                Draw the Interaction Area area on the map using the drawing tool
              </p>

              {/* @ts-ignore */}
              {(!!form.errors.area?.coordinates && form.touched.area) &&
                <FieldHelp isError>
                  <div className="flex items-center gap-2">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                    {/* @ts-ignore */}
                    {form.errors.area.coordinates}
                  </div>
                </FieldHelp>
              }
            </>
          )}
        </FormBlock>
      </FormSection>
    </Form>
  )
}

export default InteractionAreaForm;